import { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  InputText,
  InputSize,
  DateInput,
  PickerPosition,
  DateInputSize,
  Autocomplete,
  Select,
  SelectSize,
  IconUp,
  IconDown,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import { AccessRight, SignatureType } from '../../../hoc/new-application/types';
import CreateFormRow from '../../partial/create/create-form-row';
import { CompanyOfficialCreateFormTypes } from '../types';

import cn from '../../partial/create/create.module.css';

const CompanyOfficialCreateForm = ({
  control,
  errors,
  sumSubError,
  applicationSchemes,
}: CompanyOfficialCreateFormTypes) => {
  const [applicationSchemeVisible, setApplicationSchemeVisible] =
    useState(false);

  const { countries, deliveryChannelsIndividual, activePermissions } =
    useAppSelector((state) => state.customersDictionaries);

  return (
    <div className={cn.form}>
      <div className={cn.formTitle}>Contacts</div>
      <CreateFormRow title="First name">
        <Controller
          control={control}
          name="firstName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="firstName"
              className={`borderless ${
                errors?.firstName?.message ? cn.inputError : ''
              }`}
              placeholder="Enter First Name"
              onChange={onChange}
              value={value}
              error={errors?.firstName?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Last name">
        <Controller
          control={control}
          name="lastName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="lastName"
              className={`borderless ${
                errors?.lastName?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Last Name"
              onChange={onChange}
              value={value}
              error={errors?.lastName?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Phone number">
        <Controller
          control={control}
          name="phone"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="phone"
              className={`borderless ${
                errors?.phone?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Phone"
              onChange={onChange}
              value={value}
              error={errors?.phone?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="E-mail">
        <Controller
          control={control}
          name="email"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="email"
              className={`borderless ${
                errors?.email?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Email"
              onChange={onChange}
              value={value}
              error={errors?.email?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Date of Birth">
        <Controller
          control={control}
          name="dob"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <DateInput
              value={value}
              onChange={(date) => onChange(date.toISOString())}
              placeholder="Select Date Of Birth"
              error={errors?.dob?.message}
              size={DateInputSize.SMALL}
              pickerPisition={PickerPosition.RIGHT}
              pickerClassName={cn.dateOfBirthPicker}
              className={errors?.dob?.message ? cn.dateInputError : ''}
              tabIndex={0}
              birthdayPicker
              borderless
              withoutCustomSelector
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Nationality">
        <Controller
          control={control}
          name="nationalityId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              name="nationalityId"
              placeholder="Select country..."
              list={countries || []}
              value={value}
              className={`${cn.createIndividualAutocomplete} ${
                errors?.nationalityId?.message ? cn.selectError : ''
              }`}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(e.target.value)}
              error={errors?.nationalityId?.message}
              withClearButton
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Delivery channels">
        <Controller
          control={control}
          name="deliveryChannelId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Select
              name="deliveryChannelId"
              placeholder="Select an option"
              list={deliveryChannelsIndividual}
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(e.target.value)}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Signature Type">
        <Controller
          control={control}
          name="signatureType"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Select
              name="signatureType"
              placeholder="Select Signature Type"
              list={[
                {
                  name: 'None',
                  value: SignatureType.NONE,
                },
                {
                  name: 'Single',
                  value: SignatureType.SINGLE,
                },
              ]}
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(e.target.value)}
              error={errors?.signatureType?.message}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="SumSub application ID">
        <Controller
          control={control}
          name="applicantId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="applicantId"
              className="borderless"
              placeholder="Enter SumSub application ID"
              onChange={onChange}
              value={value}
              error={errors?.applicantId?.message || sumSubError}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Access Right">
        <Controller
          control={control}
          name="accessRight"
          defaultValue={AccessRight.VIEWER}
          render={({ field: { onChange, value } }) => (
            <Select
              name="accessRight"
              placeholder="Select Access Right"
              list={[
                {
                  name: 'None',
                  value: AccessRight.NONE,
                },
                {
                  name: 'Viewer',
                  value: AccessRight.VIEWER,
                },
              ]}
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(e.target.value)}
              error={errors?.accessRight?.message}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="US Citizen">
        <Controller
          control={control}
          name="isUSResident"
          render={({ field: { onChange, value } }) => (
            <Select
              name="US Citizen"
              placeholder="Select option..."
              list={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' },
              ]}
              value={value ? 'yes' : 'no'}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(Boolean(e.target.value === 'yes'))}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Participation in politics">
        <Controller
          control={control}
          name="participatesInPolitics"
          render={({ field: { onChange, value } }) => (
            <Select
              name="participatesInPolitics"
              placeholder="Select option"
              list={[
                { name: 'Yes, I am participating', value: 'yes' },
                { name: 'Not participating', value: 'no' },
              ]}
              size={SelectSize.SMALL}
              value={value ? 'yes' : 'no'}
              onChange={(e) => onChange(Boolean(e.target.value === 'yes'))}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <button
        type="button"
        className={`${cn.formTitle} ${cn.formTitleButton}`}
        onClick={() => setApplicationSchemeVisible(!applicationSchemeVisible)}
      >
        {applicationSchemeVisible ? <IconUp /> : <IconDown />}
        Application Scheme
      </button>
      {applicationSchemeVisible &&
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_SCHEMES_LIST,
        ) && (
          <CreateFormRow title="COF scheme" id="applicationSchemeId">
            <Controller
              control={control}
              name="applicationSchemeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="applicationSchemeId"
                  placeholder="Select an option"
                  list={applicationSchemes?.map((item) => ({
                    name: item.name,
                    value: item.id,
                  }))}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={onChange}
                  tabIndex={0}
                  error={errors?.applicationSchemeId?.message}
                  withClearButton
                  borderless
                />
              )}
            />
          </CreateFormRow>
        )}
      {applicationSchemeVisible &&
        !activePermissions?.includes(
          PermissionEnum.APPLICATIONS_SCHEMES_LIST,
        ) && (
          <div className={cn.applicationSchemeDenied}>
            You don`t have access to this section
          </div>
        )}
    </div>
  );
};

export default CompanyOfficialCreateForm;
